<template>
  <mobile-responsive-shell :title="title">
    <div class="sal-mobile">
      <div class="sal-mobile-toolbar">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="$router.push({ name: 'mobile-sales-create' })"
        >
          <i class="bi bi-plus-lg"></i> Vente
        </button>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="$router.push({ name: 'mobile-sales-items-create' })"
        >
          <i class="bi bi-box-seam"></i> Article
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :class="{ active: showFilters }"
          @click="showFilters = !showFilters"
        >
          <i class="bi bi-funnel"></i> Filtres
        </button>
      </div>

      <div v-show="showFilters" class="sal-mobile-filters card card-body py-3 mb-3">
        <div class="mb-2">
          <label class="form-label small mb-0">Client</label>
          <select v-model="filter.customer_reference" class="form-select form-select-sm">
            <option :value="null">—</option>
            <option
              v-for="c in customers"
              :key="c.reference"
              :value="c.reference"
            >
              {{ c.fullName }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Produit</label>
          <select v-model="filter.item_reference" class="form-select form-select-sm">
            <option :value="null">—</option>
            <option
              v-for="it in items"
              :key="it.reference"
              :value="it.reference"
            >
              {{ it.fullName }}
            </option>
          </select>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-6">
            <label class="form-label small mb-0">Du</label>
            <input v-model="filter.startDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-6">
            <label class="form-label small mb-0">Au</label>
            <input v-model="filter.endDate" type="date" class="form-control form-control-sm" />
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Statut paiement</label>
          <select v-model="filter.paymentStatus" class="form-select form-select-sm">
            <option :value="null">—</option>
            <option value="paid">Payé</option>
            <option value="Unpaid">Impayé</option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-primary btn-sm flex-fill" @click="applyFilter">
            Appliquer
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm flex-fill" @click="resetFilter">
            Réinit.
          </button>
        </div>
      </div>

      <p v-if="!sales || !sales.length" class="text-muted text-center py-4">
        Aucune vente.
      </p>

      <div v-else class="sal-mobile-list">
        <article
          v-for="(row, idx) in sales"
          :key="row.reference || idx"
          class="sal-mobile-card"
        >
          <header class="sal-mobile-card-head">
            <div>
              <span class="sal-mobile-ref">{{ row.reference }}</span>
              <span
                v-if="row.invoiceReference"
                class="sal-inv ms-1"
                :class="row.invoiceReference !== 'Non Généré' ? 'sal-inv--ok' : 'sal-inv--no'"
              >
                {{ row.invoiceReference }}
              </span>
            </div>
            <div class="d-flex gap-1">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                title="Imprimer BL"
                @click="print(row.reference)"
              >
                <i class="bi bi-printer"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="goDetails(row)"
              >
                <i class="bi bi-eye"></i>
              </button>
            </div>
          </header>
          <div class="sal-mobile-meta">
            {{ row.customerFullName || "—" }} · {{ row.date || "—" }}
          </div>
          <div class="sal-mobile-stats">
            <div>
              <span class="sal-lbl">TTC</span>
              <span class="text-primary fw-semibold">{{ fmt(row.totalPriceTTC) }}</span>
            </div>
            <div>
              <span class="sal-lbl">Qté</span>
              <span>{{ fmt(row.totalQuantity) }}</span>
            </div>
            <div>
              <span class="sal-lbl">Payé</span>
              <span :class="paidClass(row)">{{ paidLabel(row) }}</span>
            </div>
            <div>
              <span class="sal-lbl">Impayé</span>
              <span class="text-danger">{{ fmt(row.amountUnpaid) }}</span>
            </div>
          </div>
          <div v-if="row.order_reference || row.passPermit || row.remark" class="sal-mobile-extra small text-muted">
            <span v-if="row.order_reference">BC {{ row.order_reference }}</span>
            <span v-if="row.passPermit"> · LP{{ row.passPermit }}</span>
            <span v-if="row.remark" class="d-block text-truncate">NB: {{ row.remark }}</span>
          </div>
          <footer v-if="row.userName" class="sal-mobile-foot text-muted small">
            {{ row.userName }}
          </footer>
        </article>
      </div>

      <div v-if="sales && sales.length" class="sal-mobile-summary">
        <div>Qté Σ <strong>{{ fmt(totalQuantity) }}</strong></div>
        <div>TTC Σ <strong>{{ fmt(totalPriceTTC) }}</strong></div>
        <div>Payé Σ <strong>{{ fmt(totalPaid) }}</strong></div>
        <div>Impayé Σ <strong>{{ fmt(totalUnpaid) }}</strong></div>
      </div>
    </div>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

export default {
  name: "MobileSalesListing",
  components: { MobileResponsiveShell },
  data() {
    return {
      title: "Ventes",
      showFilters: false,
      filter: {
        customer_reference: null,
        item_reference: null,
        startDate: null,
        endDate: null,
        paymentStatus: null,
      },
    };
  },
  computed: {
    ...mapGetters("sale", {
      sales: "getAll",
      totalQuantity: "getSalesTotalQuantity",
      totalPriceTTC: "getSalesTotalPriceTTC",
      totalPaid: "getSalesTotalPaid",
      totalUnpaid: "getSalesTotalUnpaid",
    }),
    ...mapGetters("customer", { customers: "getAll" }),
    ...mapGetters("item", { items: "getAll" }),
  },
  created() {
    this.$store.dispatch("customer/getAll");
    this.$store.dispatch("item/getAll");
  },
  mounted() {
    const q = this.$route.query;
    if (q.item_reference || q.customer_reference || q.startDate || q.endDate) {
      this.filter = {
        ...this.filter,
        item_reference: q.item_reference || null,
        customer_reference: q.customer_reference || null,
        startDate: q.startDate || null,
        endDate: q.endDate || null,
      };
      this.applyFilter(false);
    } else {
      this.$store.dispatch("sale/fetchSales");
    }
  },
  methods: {
    fmt(v) {
      if (v === undefined || v === null || v === "") return "—";
      const n = parseFloat(v);
      if (Number.isNaN(n)) return String(v);
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    paidLabel(row) {
      const paid = parseFloat(row.amountPaid);
      if (!Number.isNaN(paid) && paid > 0) return this.fmt(row.amountPaid);
      return "Impayé";
    },
    paidClass(row) {
      const paid = parseFloat(row.amountPaid);
      const ttc = parseFloat(row.totalPriceTTC);
      if (!Number.isNaN(paid) && paid > 0 && !Number.isNaN(ttc) && paid === ttc) {
        return "text-success";
      }
      if (!Number.isNaN(paid) && paid > 0) return "text-primary";
      return "text-danger";
    },
    async refresh() {
      await this.$store.dispatch("sale/fetchSales");
      await this.$store.dispatch("customer/getAll");
      await this.$store.dispatch("item/getAll");
    },
    async applyFilter(pushQuery) {
      if (pushQuery !== false) {
        await this.$router
          .push({
            name: "mobile-sales-index",
            query: {
              item_reference: this.filter.item_reference || undefined,
              customer_reference: this.filter.customer_reference || undefined,
              startDate: this.filter.startDate || undefined,
              endDate: this.filter.endDate || undefined,
            },
          })
          .catch(() => {});
      }
      await this.$store.dispatch("sale/filter", { ...this.filter });
    },
    async resetFilter() {
      this.filter = {
        customer_reference: null,
        item_reference: null,
        startDate: null,
        endDate: null,
        paymentStatus: null,
      };
      await this.$router.replace({ name: "mobile-sales-index", query: {} }).catch(() => {});
      await this.$store.dispatch("sale/fetchSales");
    },
    goDetails(row) {
      this.$router.push({
        name: "mobile-sales-details",
        params: { reference: row.reference },
      });
    },
    async print(reference) {
      await this.$store.dispatch("sale/print", reference);
    },
  },
};
</script>

<style scoped>
.sal-mobile-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.sal-mobile-filters {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.sal-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.sal-mobile-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.sal-mobile-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.sal-mobile-ref {
  font-weight: 700;
  font-size: 1.1rem;
  word-break: break-all;
}
.sal-inv {
  font-size: 0.8rem;
  font-weight: 600;
  vertical-align: middle;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  display: inline-block;
}
.sal-inv--ok {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}
.sal-inv--no {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}
.sal-mobile-meta {
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0.5rem;
}
.sal-mobile-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem 0.75rem;
  font-size: 1rem;
}
.sal-lbl {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6c757d;
}
.sal-mobile-extra {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eee;
}
.sal-mobile-foot {
  margin-top: 0.35rem;
}
.sal-mobile-summary {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 0.95rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
}
</style>
